import {
  getGetNodoPortaleQueryKey,
  NodoDto,
  RegistroDto,
  useActionDispositivo,
  useGetRegistriDispositivo,
} from "@/api";
import { SmallLoading } from "@/components/Loading";
import Button from "@/elements/Button";
import { portaleConfig } from "@/portaliConfig";
import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toBool } from "../../utils";

export type GFL_TH_4000_Action =
  | "start tfl"
  | "stop tfl"
  | "start s hc"
  | "stop s hc"
  | "start t hc"
  | "stop t hc"
  | "start auto"
  | "stop auto"
  | "reset";

function registriMap(registri: RegistroDto[] | undefined) {
  return Object.fromEntries((registri ?? []).map((r) => [r.registro, r.value]));
}

export function GFL_TH_4000_Buttons({ nodo }: { nodo: NodoDto }) {
  const { t } = useTranslation();
  const [sentAction, setSentAction] = useState<GFL_TH_4000_Action | null>(null);
  const { data: registri } = useGetRegistriDispositivo(nodo.id);
  const { mutateAsync: setActionAsync, isLoading: actionLoading } =
    useActionDispositivo();
  const queryClient = useQueryClient();

  async function setAction(action: GFL_TH_4000_Action) {
    try {
      setSentAction(action);
      const response = await setActionAsync({
        id: nodo.id,
        data: { action },
      });

      if (response.data?.updated) {
        toast.success(t("dispositivoActions.toast.success"));
      } else {
        toast.warn(t("dispositivoActions.toast.noResponse"));
      }
    } catch {
      toast.error(t("dispositivoActions.toast.error"));
    } finally {
      setSentAction(null);
      queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
    }
  }

  const status = (nodo.dispositivoLastStatus ?? "").trim().toLowerCase();
  const values = registriMap(registri?.data);
  // mail di Andrea Daldoss del 03/08/2026. funzman = 1 => funzionamento automatico
  const manualMode = !toBool(values.funzman);
  const statoTfl = toBool(nodo.dispositivoLastInfo?.statoptfl);
  const statoShc = toBool(nodo.dispositivoLastInfo?.statopshc);
  const statoThc = toBool(nodo.dispositivoLastInfo?.statopthc);
  const anyManualPumpRunning = statoTfl || statoShc || statoThc;
  const controlsLocked = actionLoading;

  const canReset = status === "alarm";
  const canStartAuto =
    !manualMode &&
    !anyManualPumpRunning &&
    status !== "alarm" &&
    status !== "on";
  const canStopAuto = status === "on";

  const canStartManual = manualMode && status !== "alarm" && status !== "on";
  const canStopManual = manualMode && status !== "alarm";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStartAuto}
            onClick={() => setAction("start auto")}
            startIcon={
              sentAction === "start auto" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Start auto
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStopAuto}
            onClick={() => setAction("stop auto")}
            startIcon={
              sentAction === "stop auto" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Stop auto
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canReset}
            onClick={() => setAction("reset")}
            startIcon={
              sentAction === "reset" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("azioni.reset")}
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStartManual || statoTfl}
            onClick={() => setAction("start tfl")}
            startIcon={
              sentAction === "start tfl" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Start TFL700
          </Button>
        </Grid>
        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStopManual || !statoTfl}
            onClick={() => setAction("stop tfl")}
            startIcon={
              sentAction === "stop tfl" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Stop TFL700
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStartManual || statoShc}
            onClick={() => setAction("start s hc")}
            startIcon={
              sentAction === "start s hc" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Start scarico HC400
          </Button>
        </Grid>
        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStopManual || !statoShc}
            onClick={() => setAction("stop s hc")}
            startIcon={
              sentAction === "stop s hc" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Stop scarico HC400
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStartManual || statoThc}
            onClick={() => setAction("start t hc")}
            startIcon={
              sentAction === "start t hc" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Start travaso HC400
          </Button>
        </Grid>
        <Grid item xs={6} md={4}>
          <Button
            size="small"
            variant="contained"
            disabled={controlsLocked || !canStopManual || !statoThc}
            onClick={() => setAction("stop t hc")}
            startIcon={
              sentAction === "stop t hc" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Stop travaso HC400
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
